/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Spin, notification } from 'antd';
import get from 'lodash/get';
import {
  DAILY_AI_FEATURE_IDS,
  SPACER_LOG_AI_FEATURES,
  fetchAiInsightFeatures,
  generateAiInsight,
} from '../../services/aiInsightsApi';
import SpacerLogMarkdown from './SpacerLogMarkdown';
import SpacerLogAiCitations from './SpacerLogAiCitations';
import SpacerLogAiAnswerActions from './SpacerLogAiAnswerActions';
import SpacerLogAiInsightsSidebar from './SpacerLogAiInsightsSidebar';

/** Display titles for sidebar (API titles may be shorter). */
const FEATURE_DISPLAY = {
  [SPACER_LOG_AI_FEATURES.PERIOD_BRIEF]: {
    title: 'Period management brief',
    description: 'Month/quarter/year narrative from overview KPIs and pipeline comparison.',
  },
  [SPACER_LOG_AI_FEATURES.SHIFT_BRIEF]: {
    title: 'Shift / day superintendent brief',
    description: 'Narrative summary of daily KPIs, issues, bottlenecks, and actions.',
  },
  [SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH]: {
    title: 'Bottleneck / pace coach',
    description: 'Coaching on joint stage pace and bottlenecks.',
  },
  [SPACER_LOG_AI_FEATURES.COMMENT_THEMES]: {
    title: 'Downtime & comment themes',
    description: 'Themes across downtime comments for the day.',
  },
  [SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT]: {
    title: 'Close-out / compliance assistant',
    description: 'Compliance gaps for the selected day in plain language.',
  },
  [SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN]: {
    title: 'Data-quality anomaly explain',
    description: 'Explain unusual joint or KPI patterns for the day.',
  },
};

const FALLBACK_FEATURES = [
  {
    feature: SPACER_LOG_AI_FEATURES.PERIOD_BRIEF,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.PERIOD_BRIEF],
    maturity: 'P0',
    requiredContext: ['projectId', 'anchorDate', 'periodType'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.SHIFT_BRIEF,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.SHIFT_BRIEF],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.BOTTLENECK_COACH],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMMENT_THEMES,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.COMMENT_THEMES],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.COMPLIANCE_ASSISTANT],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
  {
    feature: SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN,
    ...FEATURE_DISPLAY[SPACER_LOG_AI_FEATURES.ANOMALY_EXPLAIN],
    maturity: 'P0',
    requiredContext: ['projectId', 'sowId', 'logDate'],
    status: 'live',
  },
];

const withDisplayMeta = (f) => {
  const display = FEATURE_DISPLAY[f.feature];
  if (!display) return f;
  return {
    ...f,
    title: display.title,
    description: f.description || display.description,
  };
};

const sessionCache = new Map();

const cacheKey = (feature, ctx) =>
  [
    feature,
    ctx.projectId,
    ctx.sowId ?? '',
    ctx.logDate ?? '',
    ctx.shift ?? '',
    ctx.anchorDate ?? '',
    ctx.periodType ?? '',
  ].join(':');

const normalizeShift = (shift) => {
  if (shift === '0' || shift === 0) return 0;
  if (shift === '1' || shift === 1) return 1;
  return 'both';
};

const missingContext = (required = [], ctx) => {
  const missing = [];
  required.forEach((key) => {
    const val = ctx[key];
    if (val == null || val === '') {
      missing.push(key);
      return;
    }
    if (key === 'sowId' && (val === 'all' || Number.isNaN(Number(val)))) {
      missing.push('sowId (select a pipeline)');
    }
  });
  return missing;
};

const SpacerLogAiInsightPanel = ({
  open,
  initialFeature,
  tabScope = 'daily',
  context = {},
  onQuota,
}) => {
  const [featuresMeta, setFeaturesMeta] = useState(null);
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(initialFeature || SPACER_LOG_AI_FEATURES.SHIFT_BRIEF);
  const [result, setResult] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const abortRef = useRef(null);
  const timerRef = useRef(null);

  const aiEnabled = featuresMeta ? featuresMeta.enabled !== false : true;

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingFeatures(true);
    fetchAiInsightFeatures()
      .then((data) => {
        if (!cancelled) {
          setFeaturesMeta(data);
          if (typeof onQuota === 'function' && data.quota) onQuota(data.quota);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFeaturesMeta({
            enabled: false,
            ragConfigured: false,
            features: FALLBACK_FEATURES,
            provider: null,
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingFeatures(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (open && initialFeature) {
      setSelectedFeature(initialFeature);
      setResult(null);
    }
  }, [open, initialFeature]);

  useEffect(() => {
    if (!open) {
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setGenerating(false);
      setElapsedSec(0);
    }
  }, [open]);

  const scopedFeatures = useMemo(() => {
    const apiList = featuresMeta?.features?.length ? featuresMeta.features : FALLBACK_FEATURES;
    const allow =
      tabScope === 'overview'
        ? new Set([SPACER_LOG_AI_FEATURES.PERIOD_BRIEF])
        : new Set(DAILY_AI_FEATURE_IDS);
    return apiList.filter((f) => allow.has(f.feature)).map(withDisplayMeta);
  }, [featuresMeta, tabScope]);

  useEffect(() => {
    if (!scopedFeatures.length) return;
    if (!scopedFeatures.some((f) => f.feature === selectedFeature)) {
      setSelectedFeature(scopedFeatures[0].feature);
    }
  }, [scopedFeatures, selectedFeature]);

  useEffect(() => {
    if (!open || !selectedFeature) return;
    const key = cacheKey(selectedFeature, context);
    if (sessionCache.has(key)) {
      const cached = sessionCache.get(key);
      setResult(cached);
      if (typeof onQuota === 'function' && cached?.quota) onQuota(cached.quota);
    }
    // Intentionally key off primitive context fields (parent builds a new object each render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    selectedFeature,
    context.projectId,
    context.sowId,
    context.logDate,
    context.shift,
    context.anchorDate,
    context.periodType,
  ]);

  const selectedMeta = scopedFeatures.find((f) => f.feature === selectedFeature) || null;
  const isStub = selectedMeta?.status === 'stub';
  const ctxMissing = missingContext(selectedMeta?.requiredContext || [], context);

  const buildBody = useCallback(
    (mode) => {
      const body = {
        feature: selectedFeature,
        mode,
        projectId: context.projectId,
      };
      if (tabScope === 'overview') {
        body.anchorDate = context.anchorDate;
        body.periodType = context.periodType;
        if (context.sowId != null) body.sowId = context.sowId;
      } else {
        body.sowId = Number(context.sowId);
        body.logDate = context.logDate;
        body.shift = normalizeShift(context.shift);
      }
      return body;
    },
    [selectedFeature, context, tabScope]
  );

  const runGenerate = useCallback(
    async (mode = 'ai') => {
      if (!selectedFeature || ctxMissing.length) return;
      if (mode === 'ai' && !aiEnabled) {
        mode = 'template';
      }
      if (isStub && mode === 'ai') {
        notification.info({ message: 'This insight is coming soon.' });
        return;
      }

      const key = cacheKey(selectedFeature, context);
      if (mode === 'ai' && sessionCache.has(key)) {
        const cached = sessionCache.get(key);
        setResult(cached);
        if (typeof onQuota === 'function' && cached?.quota) onQuota(cached.quota);
        return;
      }

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setGenerating(true);
      setElapsedSec(0);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => setElapsedSec((s) => s + 1), 1000);

      try {
        const data = await generateAiInsight(buildBody(mode), { signal: controller.signal });
        setResult(data);
        if (typeof onQuota === 'function' && data?.quota) onQuota(data.quota);
        if (mode === 'ai' && data?.markdown) {
          sessionCache.set(key, data);
        }
      } catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || err?.name === 'AbortError') {
          return;
        }
        const msg = get(err, 'error', get(err, 'message', 'Failed to generate insight.'));
        notification.error({ message: msg });
      } finally {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setGenerating(false);
        abortRef.current = null;
      }
    },
    [selectedFeature, ctxMissing.length, aiEnabled, isStub, context, buildBody, onQuota]
  );

  const handleCancel = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleCopy = async () => {
    const md = result?.markdown;
    if (!md) return;
    try {
      await navigator.clipboard.writeText(md);
      notification.success({ message: 'Copied insight to clipboard.' });
    } catch {
      notification.error({ message: 'Could not copy to clipboard.' });
    }
  };

  const footerMeta = useMemo(() => {
    if (!result) return null;
    const parts = [];
    if (result.generatedAt) {
      try {
        parts.push(
          new Date(result.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        );
      } catch {
        parts.push(result.generatedAt);
      }
    }
    if (result.provider) parts.push(result.provider);
    if (result.mode === 'template') parts.push('template');
    // Per-answer INPUT/OUTPUT/COST is shown under the brief via SpacerLogAiAnswerActions
    return parts.join(' · ');
  }, [result]);

  const showFallbackBanner =
    result && (result.fallback === true || result.mode === 'template');

  if (!open) return null;

  const handleSelectFeature = (featureId) => {
    if (!featureId || featureId === selectedFeature) return;
    setSelectedFeature(featureId);
    setResult(null);
  };

  return (
    <div className="spacer-ask-evans__workspace spacer-ask-evans__workspace--insights">
      <SpacerLogAiInsightsSidebar
        features={scopedFeatures}
        activeFeature={selectedFeature}
        loading={loadingFeatures && !scopedFeatures.length}
        onSelect={handleSelectFeature}
      />

      <div className="spacer-ask-evans__main">
        <div className="spacer-log-ai-panel spacer-log-ai-panel--ask-evans spacer-log-ai-panel--insights-layout">
          {!aiEnabled ? (
            <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
              AI temporarily unavailable — template briefs still work.
            </div>
          ) : null}

          {selectedMeta?.title ? (
            <h3 className="spacer-log-ai-brief-title">{selectedMeta.title}</h3>
          ) : null}

          {selectedMeta?.description ? (
            <p className="spacer-log-ai-desc">{selectedMeta.description}</p>
          ) : null}

          {ctxMissing.length ? (
            <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
              {tabScope === 'daily' && ctxMissing.some((m) => String(m).includes('sowId'))
                ? 'Select a pipeline day first.'
                : `Missing context: ${ctxMissing.join(', ')}`}
            </div>
          ) : null}

          {generating ? (
            <div className="spacer-log-ai-loading">
              <Spin size="small" />
              <span>Generating… {elapsedSec}s</span>
            </div>
          ) : null}

          {showFallbackBanner ? (
            <div className="spacer-log-ai-banner spacer-log-ai-banner--warn">
              {result.fallback
                ? 'AI unavailable — showing template brief'
                : 'Deterministic brief (template)'}
              {result.error ? ` · ${result.error}` : ''}
            </div>
          ) : null}

          <div className="spacer-log-ai-body">
            {result?.markdown ? (
              <>
                <div className="spacer-ask-evans__bubble spacer-ask-evans__bubble--bot spacer-ask-evans__bubble--brief">
                  <SpacerLogMarkdown content={result.markdown} />
                  <SpacerLogAiCitations citations={result.citations} />
                  <SpacerLogAiAnswerActions
                    markdown={result.markdown}
                    usage={result.usage}
                    feedbackTargetId={result.feedbackTargetId}
                    feature={selectedFeature}
                    projectId={context.projectId}
                    ragSessionId={result.ragSessionId || result.sessionId}
                    title="Spacer Log Insights Brief"
                    eyebrow="SPACER LOG - ASK EVANS REPORT"
                    subtitle="Prepared for operations - CONFIDENTIAL"
                  />
                </div>
              </>
            ) : !generating ? (
              <div className="spacer-ask-evans__empty spacer-ask-evans__empty--insights">
                <div className="spacer-ask-evans__empty-avatar" aria-hidden>
                  AE
                </div>
                <div className="spacer-ask-evans__empty-title">
                  {selectedMeta?.title || 'Insights'}
                </div>
                <p className="spacer-ask-evans__empty-text">
                  Pick a brief on the left, then Generate. Uses your current Spacer Log filters.
                </p>
              </div>
            ) : null}
          </div>

          {result?.markdown ? (
            <div className="spacer-log-ai-footer">
              <span className="spacer-log-ai-footer__meta">{footerMeta || '—'}</span>
              <div className="spacer-log-ai-footer__actions">
                <button type="button" className="spacer-log-ai-btn spacer-log-ai-btn--standard" onClick={handleCopy}>
                  Copy
                </button>
                <button
                  type="button"
                  className="spacer-log-ai-btn spacer-log-ai-btn--standard"
                  disabled={generating || !!ctxMissing.length}
                  onClick={() => {
                    sessionCache.delete(cacheKey(selectedFeature, context));
                    runGenerate(aiEnabled ? 'ai' : 'template');
                  }}
                >
                  Regenerate
                </button>
              </div>
            </div>
          ) : null}

          <div className="spacer-log-ai-actions spacer-log-ai-actions--bottom">
            {generating ? (
              <button
                type="button"
                className="spacer-log-ai-btn spacer-log-ai-btn--standard"
                onClick={handleCancel}
              >
                Cancel
              </button>
            ) : null}
            <button
              type="button"
              className="spacer-log-ai-btn spacer-log-ai-btn--standard"
              disabled={generating || !!ctxMissing.length}
              onClick={() => runGenerate('template')}
            >
              Template only
            </button>
            <button
              type="button"
              className="spacer-log-ai-btn spacer-log-ai-btn--primary"
              disabled={generating || !!ctxMissing.length || isStub}
              onClick={() => runGenerate(aiEnabled ? 'ai' : 'template')}
            >
              Generate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpacerLogAiInsightPanel;
